import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Switch,
  Typography,
} from "@mui/material";
import {
  GridColDef,
  GridColumnGroupingModel,
  GridExpandMoreIcon,
} from "@mui/x-data-grid";
import { GKClientDataGrid, Grid2 } from "components";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import {
  Navigate,
  createSearchParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { extraColumnsField } from "utils/helpers";

const formatValue = (value?: number | string | null) =>
  value === null || value === undefined || value === "" ? "-" : value;

export const TRAILING_PERIODS = [
  { key: "1y", label: "1 yr" },
  { key: "3y", label: "3 yr" },
  { key: "5y", label: "5 yr" },
  { key: "10y", label: "10 yr" },
  { key: "sinceInception", label: "Since Inception" },
];

export const RISK_RATIOS = [
  { key: "informationRatio", label: "Information" },
  { key: "sharpe", label: "Sharpe" },
  { key: "sortino", label: "Sortino" },
];

export const RISK_HORIZONS = ["1", "3", "5"];

export const SectionAccordion = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Accordion
    disableGutters
    style={{
      border: `solid 1px var(--neutral-100)`,
      borderRadius: "8px",
      boxShadow: "none",
      background: "none",
    }}
  >
    <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
      <Typography fontSize={16} fontWeight={600}>
        {title}
      </Typography>
    </AccordionSummary>
    <Divider />
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

export const OTHER_FIELDS = [
  { key: "maxDrawdown", label: "Max Drawdown (%)" },
  { key: "recoveryWeeks", label: "Recovery Period (Weeks)" },
  { key: "downsideCapture", label: "Downside Captured" },
  { key: "upsideCapture", label: "Upside Captured" },
  { key: "pe", label: "P/E" },
  { key: "pb", label: "P/B" },
];

// Funds and benchmark nest these under drawdown/capture, category median has them flat
export const getOtherValues = (item: any) => ({
  maxDrawdown:
    item?.risk?.drawdown?.maxDrawdownPct ?? item?.risk?.maxDrawdownPct,
  recoveryWeeks:
    item?.risk?.drawdown?.recoveryWeeks ?? item?.risk?.recoveryWeeks,
  downsideCapture:
    item?.risk?.capture?.downsideCapture ?? item?.risk?.downsideCapture,
  upsideCapture:
    item?.risk?.capture?.upsideCapture ?? item?.risk?.upsideCapture,
  pe: item?.pe,
  pb: item?.pb,
});

const ROLLING_STATS = [
  { key: "average", label: "Average" },
  { key: "median", label: "Median" },
  { key: "maximum", label: "Maximum" },
  { key: "minimum", label: "Minimum" },
];

// Rolling return columns (stats + distribution buckets) after the given leading columns.
// Rows are the item's rolling.<horizon> object
export const getRollingTable = (
  response: any,
  leadingColumns: GridColDef[]
) => {
  const bucketEdges: number[] = response?.meta?.bucketEdges || [];
  const bucketLabels = bucketEdges.length
    ? [
        `Less than ${bucketEdges[0]}%`,
        ...bucketEdges
          .slice(0, -1)
          .map((edge, index) => `${edge} - ${bucketEdges[index + 1]}%`),
        `Greater than ${bucketEdges[bucketEdges.length - 1]}%`,
      ]
    : [];

  const rollingColumns: GridColDef[] = [
    ...leadingColumns,
    ...ROLLING_STATS.map((stat) => ({
      ...extraColumnsField(stat.label, stat.key, 100, 0),
      valueGetter: (row: any) => formatValue(row?.row?.[stat.key]),
    })),
    ...bucketLabels.map((label) => ({
      ...extraColumnsField(label, label, 110, 0),
      valueGetter: (row: any) => formatValue(row?.row?.distribution?.[label]),
    })),
  ];

  const rollingGrouping: GridColumnGroupingModel = [
    {
      groupId: "Return Statistics (%)",
      children: ROLLING_STATS.map((stat) => ({ field: stat.key })),
    },
    {
      groupId: "Return distribution (% of times)",
      children: bucketLabels.map((label) => ({ field: label })),
    },
  ];

  return { rollingColumns, rollingGrouping };
};

// topTen.<period> true means the fund ranks in the category's top ten.
// The grid overrides its own sx, so wrap it in a Box with topTenSx
export const topTenClassName = (key: string) => (params: any) =>
  params?.row?.topTen?.[key] ? "mf-top-ten" : "";

export const topTenSx = {
  // Alpha rows sit under each scheme in a muted shade
  "& .MuiDataGrid-row.mf-alpha-row": {
    background: "var(--neutral-50)",
    color: "var(--neutral-500)",
    fontStyle: "italic",
  },
  "& .MuiDataGrid-cell.mf-top-ten": {
    background: "var(--success-100)",
    color: "var(--success-700)",
    fontWeight: 600,
  },
};

// Alpha row ids end with "-alpha", see the trailing returns tables
export const alphaRowClassName = (params: any) =>
  String(params.id).endsWith("-alpha") ? "mf-alpha-row" : "";

// Calendar returns switch: on = yearly, off = quarterly
export const PeriodToggle = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Grid2 display="flex" alignItems="center" justifyContent="flex-end" mb={1}>
    <Typography fontSize={14}>Quarterly</Typography>
    <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <Typography fontSize={14}>Yearly</Typography>
  </Grid2>
);

const nameColumn: GridColDef = {
  ...extraColumnsField("Scheme / Category Name", "name", 300, 2),
};

const MFReturns = () => {
  const navigate = useNavigate();
  // The MF page passes the category table response with only the selected funds
  const { state } = useLocation();
  const category: string = state?.category || "";
  const response = state?.response;
  const funds: any[] = response?.funds || [];
  const [showYearly, setShowYearly] = useState(false);
  const benchmarkLabel = response?.benchmark?.name || response?.meta?.benchmark;
  const benchmarkName = benchmarkLabel
    ? `Benchmark (${benchmarkLabel})`
    : "Benchmark";

  // Benchmark and category median share the fund shape (returns, rolling, risk)
  const summaries = [
    { id: "benchmark", name: benchmarkName, data: response?.benchmark },
    {
      id: "category-median",
      name: "Category Median",
      data: response?.categoryMedian,
    },
  ];

  // One row per fund, then benchmark and category median.
  // Spread first: some sections carry their own `name`, keep our labels
  const buildRows = (getValues: (item: any) => Record<string, any>) => [
    ...funds.map((fund) => ({
      ...getValues(fund),
      id: fund.schemeCode,
      name: fund.name,
    })),
    ...summaries.map((summary) => ({
      ...getValues(summary.data),
      id: summary.id,
      name: summary.name,
    })),
  ];

  /* ---------- 1. Trailing returns ---------- */
  const trailingRows = buildRows((item) => ({
    ...item?.returns,
    topTen: item?.topTen,
  }));
  // Each scheme gets an Alpha row right below it, alpha is keyed like returns
  const trailingRowsWithAlpha = trailingRows.flatMap((row: any) => {
    const fund = funds.find((item) => item.schemeCode === row.id);
    if (!fund) return [row];
    return [
      row,
      {
        ...fund?.alpha,
        topTen: fund?.alphaTopTen,
        id: `${row.id}-alpha`,
        name: "Alpha",
      },
    ];
  });
  const trailingColumns: GridColDef[] = [
    nameColumn,
    ...TRAILING_PERIODS.map((period) => ({
      ...extraColumnsField(period.label, period.key, 110, 0),
      valueGetter: (row: any) => formatValue(row?.row?.[period.key]),
      cellClassName: topTenClassName(period.key),
    })),
  ];

  /* ---------- 2. Calendar returns, quarterly by default ---------- */
  const calendarPeriods: string[] =
    (showYearly ? response?.meta?.years : response?.meta?.quarters) || [];
  const calendarKey = showYearly ? "calendarReturns" : "quarterlyReturns";
  // No periods from the API (e.g. no quarters) shows an empty table
  const calendarRows = calendarPeriods.length
    ? buildRows((item) => item?.[calendarKey])
    : [];
  const calendarColumns: GridColDef[] = [
    nameColumn,
    ...calendarPeriods.map((period) => ({
      ...extraColumnsField(period, period, 90, 0),
      valueGetter: (row: any) => formatValue(row?.row?.[period]),
    })),
  ];

  /* ---------- 3 & 4. Rolling returns ---------- */
  const { rollingColumns, rollingGrouping } = getRollingTable(response, [
    nameColumn,
  ]);

  const getRollingRows = (horizon: string) =>
    buildRows((item) => item?.rolling?.[horizon]);

  // Opened directly or after a refresh there is no data, go back to MF
  if (!response) return <Navigate to="/app/mf" replace />;

  /* ---------- 5. Risk ratios ---------- */
  // risk.<ratio> comes keyed by horizon in years: { "1": .., "3": .., "5": .. }
  const ratioColumns: GridColDef[] = [
    nameColumn,
    ...RISK_RATIOS.flatMap((ratio) =>
      RISK_HORIZONS.map((horizon) => ({
        ...extraColumnsField(
          `${ratio.label} ${horizon}y`,
          `${ratio.key}-${horizon}`,
          120,
          0
        ),
        valueGetter: (row: any) =>
          formatValue(row?.row?.risk?.[ratio.key]?.[horizon]),
      }))
    ),
  ];
  const ratioRows = buildRows((item) => ({ risk: item?.risk }));

  /* ---------- 6. Others ---------- */
  const otherColumns: GridColDef[] = [
    nameColumn,
    ...OTHER_FIELDS.map((field) => ({
      ...extraColumnsField(field.label, field.key, 130, 0),
      valueGetter: (row: any) => formatValue(row?.row?.[field.key]),
    })),
  ];
  const otherRows = buildRows(getOtherValues);

  const tableProps = {
    showHeaderGrid: false,
    pagination: false,
    hideFooter: true,
    disableRowSelectionOnClick: true,
  };

  return (
    <DashboardLayout title="MF">
      <Grid2 container spacing={2}>
        <Grid2
          size={12}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography fontSize={16} fontWeight={600}>
            {category}
          </Typography>
          <Button
            variant="outlined"
            onClick={() =>
              navigate({
                pathname: "/app/mf",
                search: createSearchParams({ category }).toString(),
              })
            }
          >
            Back
          </Button>
        </Grid2>
        <Grid2 size={12}>
          <SectionAccordion title="Trailing Returns">
            <Box sx={topTenSx}>
              <GKClientDataGrid
                {...tableProps}
                columns={trailingColumns}
                rows={trailingRowsWithAlpha}
                getRowClassName={alphaRowClassName}
              />
            </Box>
          </SectionAccordion>
        </Grid2>
        <Grid2 size={12}>
          <SectionAccordion title="Calendar Year Returns">
            <PeriodToggle checked={showYearly} onChange={setShowYearly} />
            <GKClientDataGrid
              {...tableProps}
              // Remount when switching so the new period columns load fresh
              key={calendarKey}
              columns={calendarColumns}
              rows={calendarRows}
            />
          </SectionAccordion>
        </Grid2>
        <Grid2 size={12}>
          <SectionAccordion title="Rolling Return - 3 yr">
            <GKClientDataGrid
              {...tableProps}
              columns={rollingColumns}
              rows={getRollingRows("3")}
              columnGroupingModel={rollingGrouping}
            />
          </SectionAccordion>
        </Grid2>
        <Grid2 size={12}>
          <SectionAccordion title="Rolling Return - 5 yr">
            <GKClientDataGrid
              {...tableProps}
              columns={rollingColumns}
              rows={getRollingRows("5")}
              columnGroupingModel={rollingGrouping}
            />
          </SectionAccordion>
        </Grid2>
        <Grid2 size={12}>
          <SectionAccordion title="Sortino / Information / Sharpe">
            <GKClientDataGrid
              {...tableProps}
              columns={ratioColumns}
              rows={ratioRows}
            />
          </SectionAccordion>
        </Grid2>
        <Grid2 size={12}>
          <SectionAccordion title="Others Returns">
            <GKClientDataGrid
              {...tableProps}
              columns={otherColumns}
              rows={otherRows}
            />
          </SectionAccordion>
        </Grid2>
      </Grid2>
    </DashboardLayout>
  );
};

export default MFReturns;
